import {
  createApiClient,
  createQueryClient,
  QueryClientProvider,
  type QueryClient,
} from '@platform/api-client';
import { createTypedClient, type ShippingPaths } from '@platform/openapi';
import { AuthProvider, useAuth, type AuthClient } from '@platform/auth-client';
import { ThemeProvider, ToastProvider } from '@platform/design-system';
import { FlagsProvider } from '@platform/flags';
import { I18nextProvider, isLocale, useTranslation, type Locale } from '@platform/i18n';
import { createBrowserHistory, RouterProvider, type RouterHistory } from '@tanstack/react-router';
import type { i18n as I18nInstance } from 'i18next';
import { useMemo, useState } from 'react';
import type { AppConfig } from '../config/schema.ts';
import { SupportProvider } from '../shell/support.tsx';
import { createAppRouter, type ApiClients } from './router.tsx';
import { useLocaleFromUrl } from './useLocaleFromUrl.ts';

type Props = {
  config: AppConfig;
  authClient: AuthClient;
  i18n: I18nInstance;
  history?: RouterHistory;
  queryClient?: QueryClient;
};

type RoutedProps = { clients: ApiClients; history: RouterHistory; defaultLocale: Locale };

const Routed = ({ clients, history, defaultLocale }: RoutedProps) => {
  const auth = useAuth();
  const { i18n } = useTranslation();
  useLocaleFromUrl(history);
  const [router] = useState(() =>
    createAppRouter(
      clients,
      { auth },
      { history, currentLocale: () => (isLocale(i18n.language) ? i18n.language : defaultLocale) },
    ),
  );
  return <RouterProvider router={router} context={{ auth }} />;
};

export const App = ({ config, authClient, i18n, history, queryClient }: Props) => {
  const [client] = useState(() => queryClient ?? createQueryClient());
  const clients = useMemo(() => {
    const clientFor = (baseUrl: string) =>
      createApiClient({
        baseUrl,
        getAccessToken: authClient.getAccessToken,
        onUnauthorized: authClient.markExpired,
      });
    return {
      api: createTypedClient(clientFor(config.apiBaseUrl)),
      shipping: createTypedClient<ShippingPaths>(clientFor(config.shippingApiBaseUrl)),
    };
  }, [config.apiBaseUrl, config.shippingApiBaseUrl, authClient]);
  const routerHistory = useMemo(() => history ?? createBrowserHistory(), [history]);
  return (
    <I18nextProvider i18n={i18n}>
      <FlagsProvider>
        <ThemeProvider>
          <ToastProvider>
            <AuthProvider client={authClient}>
              <QueryClientProvider client={client}>
                <SupportProvider links={config.support}>
                  <Routed clients={clients} history={routerHistory} defaultLocale={config.defaultLocale} />
                </SupportProvider>
              </QueryClientProvider>
            </AuthProvider>
          </ToastProvider>
        </ThemeProvider>
      </FlagsProvider>
    </I18nextProvider>
  );
};
